import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { locationsAPI, productsAPI, extractErrorMessage } from '../api';

const TABS = ['locations', 'transfers', 'movements', 'batches'];
const LOCATION_TYPES = ['MAIN_STORE', 'WAREHOUSE', 'SHOWROOM', 'RENTAL_LOCATION'];

const Locations = () => {
  const { selectedStore } = useStore();
  const [activeTab, setActiveTab] = useState('locations');
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [batches, setBatches] = useState([]);
  const [expiringBatches, setExpiringBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [locationForm, setLocationForm] = useState({
    name: '', type: 'MAIN_STORE', code: '', address: '', phone: '', is_default: false,
  });
  const [transferForm, setTransferForm] = useState({
    from_location_id: '', to_location_id: '', product_id: '', quantity: 1, notes: '',
  });
  const [movementFilters, setMovementFilters] = useState({
    type: '', location_id: '', product_id: '', start_date: '', end_date: '',
  });

  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await locationsAPI.list(selectedStore.id);
      setLocations(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load locations'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  const loadProducts = useCallback(async () => {
    try {
      const response = await productsAPI.list(selectedStore.id);
      setProducts(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  const loadMovements = useCallback(async () => {
    try {
      const params = {};
      Object.entries(movementFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const response = await locationsAPI.getMovements(selectedStore.id, params);
      setMovements(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setMovements([]);
    }
  }, [selectedStore, movementFilters]);

  const loadBatches = useCallback(async () => {
    try {
      const [batchRes, expiringRes] = await Promise.allSettled([
        locationsAPI.listBatches(selectedStore.id),
        locationsAPI.getExpiringBatches(selectedStore.id, 30),
      ]);
      if (batchRes.status === 'fulfilled') {
        const d = batchRes.value.data;
        setBatches(Array.isArray(d) ? d : d.items || []);
      }
      if (expiringRes.status === 'fulfilled') {
        const d = expiringRes.value.data;
        setExpiringBatches(Array.isArray(d) ? d : d.items || []);
      }
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      loadLocations();
      loadProducts();
    }
  }, [selectedStore, loadLocations, loadProducts]);

  useEffect(() => {
    if (selectedStore && activeTab === 'movements') loadMovements();
    if (selectedStore && activeTab === 'batches') loadBatches();
  }, [selectedStore, activeTab, loadMovements, loadBatches]);

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await locationsAPI.update(editingId, locationForm);
      } else {
        await locationsAPI.create(selectedStore.id, locationForm);
      }
      loadLocations();
      resetLocationForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const handleLocationDelete = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    try {
      await locationsAPI.delete(id);
      loadLocations();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete location'));
    }
  };

  const handleEditLocation = (loc) => {
    setLocationForm({
      name: loc.name || '', type: loc.type || 'MAIN_STORE', code: loc.code || '',
      address: loc.address || '', phone: loc.phone || '', is_default: loc.is_default || false,
    });
    setEditingId(loc.id);
    setShowForm(true);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await locationsAPI.transfer(selectedStore.id, {
        ...transferForm,
        quantity: parseInt(transferForm.quantity),
      });
      setTransferForm({ from_location_id: '', to_location_id: '', product_id: '', quantity: 1, notes: '' });
      loadMovements();
      alert('Transfer completed!');
    } catch (err) {
      setError(extractErrorMessage(err, 'Transfer failed'));
    }
  };

  const resetLocationForm = () => {
    setLocationForm({ name: '', type: 'MAIN_STORE', code: '', address: '', phone: '', is_default: false });
    setEditingId(null);
    setShowForm(false);
    setError(null);
  };

  if (loading && activeTab === 'locations') {
    return <div className="loading"><div className="spinner"></div><span>Loading locations...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Locations</h1>
        {activeTab === 'locations' && (
          <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); if (showForm) resetLocationForm(); }}>
            {showForm ? 'Cancel' : '+ Add Location'}
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="tab-bar">
        {TABS.map(tab => (
          <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Locations Tab */}
      {activeTab === 'locations' && (
        <>
          {showForm && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <h3 className="card-title">{editingId ? 'Edit Location' : 'Add Location'}</h3>
              </div>
              <div className="card-body">
                <form onSubmit={handleLocationSubmit}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Name *</label>
                      <input type="text" className="form-input" value={locationForm.name}
                        onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Type *</label>
                      <select className="form-select" value={locationForm.type}
                        onChange={(e) => setLocationForm({ ...locationForm, type: e.target.value })} required>
                        {LOCATION_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Code</label>
                      <input type="text" className="form-input" value={locationForm.code}
                        onChange={(e) => setLocationForm({ ...locationForm, code: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input type="tel" className="form-input" value={locationForm.phone}
                        onChange={(e) => setLocationForm({ ...locationForm, phone: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Address</label>
                      <input type="text" className="form-input" value={locationForm.address}
                        onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">&nbsp;</label>
                      <div className="form-checkbox">
                        <input type="checkbox" id="is_default" checked={locationForm.is_default}
                          onChange={(e) => setLocationForm({ ...locationForm, is_default: e.target.checked })} />
                        <label htmlFor="is_default">Default Location</label>
                      </div>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">{editingId ? 'Update' : 'Create Location'}</button>
                    {editingId && <button type="button" className="btn btn-secondary" onClick={resetLocationForm}>Cancel</button>}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Mobile Cards */}
          <div className="mobile-cards hidden-desktop">
            {locations.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">📍</div><h3 className="empty-state-title">No locations</h3></div>
            ) : (
              locations.map(loc => (
                <div className="mobile-card" key={loc.id}>
                  <div className="mobile-card-header">
                    <div>
                      <div className="mobile-card-title">{loc.name}</div>
                      <span className="badge badge-info">{loc.type?.replace(/_/g, ' ')}</span>
                    </div>
                    {loc.is_default && <span className="badge badge-success">Default</span>}
                  </div>
                  {loc.code && <div className="mobile-card-row"><span className="mobile-card-label">Code</span><span>{loc.code}</span></div>}
                  {loc.address && <div className="mobile-card-row"><span className="mobile-card-label">Address</span><span>{loc.address}</span></div>}
                  <div className="mobile-card-actions">
                    <button className="btn btn-sm btn-secondary" onClick={() => handleEditLocation(loc)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleLocationDelete(loc.id)}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop Table */}
          <div className="card desktop-table">
            <div className="table-container">
              <table>
                <thead><tr><th>Name</th><th>Type</th><th>Code</th><th>Address</th><th>Default</th><th>Actions</th></tr></thead>
                <tbody>
                  {locations.map(loc => (
                    <tr key={loc.id}>
                      <td className="font-medium">{loc.name}</td>
                      <td><span className="badge badge-info">{loc.type?.replace(/_/g, ' ')}</span></td>
                      <td className="text-muted">{loc.code || '-'}</td>
                      <td>{loc.address || '-'}</td>
                      <td>{loc.is_default ? <span className="badge badge-success">Yes</span> : '-'}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-sm btn-secondary" onClick={() => handleEditLocation(loc)}>Edit</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleLocationDelete(loc.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {locations.length === 0 && <tr><td colSpan="6" className="text-center text-muted" style={{ padding: '3rem' }}>No locations found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Transfers Tab */}
      {activeTab === 'transfers' && (
        <div className="card">
          <div className="card-header"><h3 className="card-title">Stock Transfer</h3></div>
          <div className="card-body">
            <form onSubmit={handleTransfer}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">From Location *</label>
                  <select className="form-select" value={transferForm.from_location_id}
                    onChange={(e) => setTransferForm({ ...transferForm, from_location_id: parseInt(e.target.value) })} required>
                    <option value="">Select Location</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">To Location *</label>
                  <select className="form-select" value={transferForm.to_location_id}
                    onChange={(e) => setTransferForm({ ...transferForm, to_location_id: parseInt(e.target.value) })} required>
                    <option value="">Select Location</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Product *</label>
                  <select className="form-select" value={transferForm.product_id}
                    onChange={(e) => setTransferForm({ ...transferForm, product_id: parseInt(e.target.value) })} required>
                    <option value="">Select Product</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity *</label>
                  <input type="number" className="form-input" value={transferForm.quantity} min="1"
                    onChange={(e) => setTransferForm({ ...transferForm, quantity: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={transferForm.notes} rows={2}
                  onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })} />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Transfer Stock</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Movements Tab */}
      {activeTab === 'movements' && (
        <>
          <div className="filter-bar">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={movementFilters.type}
                onChange={(e) => setMovementFilters({ ...movementFilters, type: e.target.value })}>
                <option value="">All Types</option>
                <option value="transfer">Transfer</option>
                <option value="adjustment">Adjustment</option>
                <option value="receive">Receive</option>
                <option value="sale">Sale</option>
                <option value="return">Return</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <select className="form-select" value={movementFilters.location_id}
                onChange={(e) => setMovementFilters({ ...movementFilters, location_id: e.target.value })}>
                <option value="">All Locations</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input type="date" className="form-input" value={movementFilters.start_date}
                onChange={(e) => setMovementFilters({ ...movementFilters, start_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input type="date" className="form-input" value={movementFilters.end_date}
                onChange={(e) => setMovementFilters({ ...movementFilters, end_date: e.target.value })} />
            </div>
          </div>

          <div className="mobile-cards hidden-desktop">
            {movements.length === 0 ? (
              <div className="empty-state"><p>No movements found</p></div>
            ) : (
              movements.map((m, i) => (
                <div className="mobile-card" key={m.id || i}>
                  <div className="mobile-card-header">
                    <div className="mobile-card-title">{m.product_name || `Product #${m.product_id}`}</div>
                    <span className="badge badge-info">{m.type}</span>
                  </div>
                  <div className="mobile-card-row"><span className="mobile-card-label">Location</span><span>{m.location_name || '-'}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">Qty</span><span>{m.quantity}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">Date</span><span>{m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</span></div>
                </div>
              ))
            )}
          </div>

          <div className="card desktop-table">
            <div className="table-container">
              <table>
                <thead><tr><th>Date</th><th>Type</th><th>Product</th><th>Location</th><th>Qty</th><th>Reference</th></tr></thead>
                <tbody>
                  {movements.map((m, i) => (
                    <tr key={m.id || i}>
                      <td>{m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</td>
                      <td><span className="badge badge-info">{m.type}</span></td>
                      <td>{m.product_name || `#${m.product_id}`}</td>
                      <td>{m.location_name || '-'}</td>
                      <td className="font-bold">{m.quantity}</td>
                      <td className="text-muted">{m.reference || '-'}</td>
                    </tr>
                  ))}
                  {movements.length === 0 && <tr><td colSpan="6" className="text-center text-muted" style={{ padding: '3rem' }}>No movements found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Batches Tab */}
      {activeTab === 'batches' && (
        <>
          {expiringBatches.length > 0 && (
            <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
              {expiringBatches.length} batch(es) expiring within 30 days
            </div>
          )}

          <div className="mobile-cards hidden-desktop">
            {batches.length === 0 ? (
              <div className="empty-state"><p>No batches found</p></div>
            ) : (
              batches.map((b, i) => (
                <div className="mobile-card" key={b.id || i}>
                  <div className="mobile-card-header">
                    <div className="mobile-card-title">{b.batch_number || `Batch #${b.id}`}</div>
                  </div>
                  <div className="mobile-card-row"><span className="mobile-card-label">Product</span><span>{b.product_name || '-'}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">Location</span><span>{b.location_name || '-'}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">Qty</span><span>{b.quantity}</span></div>
                  {b.expiry_date && (
                    <div className="mobile-card-row"><span className="mobile-card-label">Expiry</span><span>{new Date(b.expiry_date).toLocaleDateString()}</span></div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="card desktop-table">
            <div className="table-container">
              <table>
                <thead><tr><th>Batch #</th><th>Product</th><th>Location</th><th>Qty</th><th>Expiry</th></tr></thead>
                <tbody>
                  {batches.map((b, i) => (
                    <tr key={b.id || i}>
                      <td className="font-medium">{b.batch_number || `#${b.id}`}</td>
                      <td>{b.product_name || '-'}</td>
                      <td>{b.location_name || '-'}</td>
                      <td>{b.quantity}</td>
                      <td>{b.expiry_date ? new Date(b.expiry_date).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                  {batches.length === 0 && <tr><td colSpan="5" className="text-center text-muted" style={{ padding: '3rem' }}>No batches found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Locations;
